import { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Grid,
  Fade,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  TableSortLabel
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import Info from './Info';
import LanguageSelector from './LanguageSelector';
import { useAdminLanguage } from './AdminI18n';
import useTableSort from './useTableSort';
function StandardAdmin() {
  const { t } = useAdminLanguage();
  const [standards, setStandards] = useState([]);
  const [rubrics, setRubrics] = useState([]);
  const [selectedRubrics, setSelectedRubrics] = useState([]);
  const [formData, setFormData] = useState({ title: '', reference: '', comment: '' });
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [language, setLanguage] = useState('fr');
  const [rubricSearch, setRubricSearch] = useState('');
  const [dragIndex, setDragIndex] = useState(null);

  useEffect(() => {
    fetchStandards();
    fetchRubrics();
  }, [language]);

  const fetchStandards = async () => {
    try {
      const response = await fetch(`/api/standards?lang=${language}`);
      const data = await response.json();
      setStandards(data);
      setLoading(false);
    } catch (err) {
      setError('Erreur lors du chargement des Canevas');
      setLoading(false);
    }
  };

  const fetchRubrics = async () => {
    try {
      const response = await fetch(`/api/standards/rubrics?lang=${language}&includeObsolete=true&includeHidden=true`);
      const data = await response.json();
      setRubrics(data);
    } catch (err) {
      setError('Erreur lors du chargement des rubriques');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      if (!formData.title.trim()) {
        setError('Le titre est obligatoire');
        return;
      }

      setLoading(true);
      setError('');
      setSuccess('');

      const standardData = {
        title: formData.title,
        reference: formData.reference || '',
        comment: formData.comment || '',
        rubrics: selectedRubrics
      };

      if (isEditing) {
        const response = await fetch(`/api/standards/${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...standardData, lang: language }),
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Erreur lors de l\'enregistrement du Canevas');
        }
        setSuccess('Canevas mis à jour avec succès');
      } else {
        const response = await fetch('/api/standards', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...standardData, lang: language }),
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Erreur lors de l\'enregistrement du Canevas');
        }
        setSuccess('Canevas ajouté avec succès');
      }

      setFormData({ title: '', reference: '', comment: '' });
      setSelectedRubrics([]);
      setIsEditing(false);
      setEditingId(null);
      fetchStandards();
    } catch (err) {
      setError(err.message || 'Erreur lors de l\'enregistrement du Canevas');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (standard) => {
    setIsEditing(true);
    setEditingId(standard.id);
    setFormData({
      title: standard.title || '',
      reference: standard.reference || '',
      comment: standard.comment || '',
    });
    setSelectedRubrics(standard.rubrics);
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await fetch(`/api/standards/${id}?lang=${language}`, {
        method: 'DELETE',
      });
      setSuccess('Canevas supprimé avec succès');
      fetchStandards();
    } catch (err) {
      setError('Erreur lors de la suppression du Canevas');
    } finally {
      setLoading(false);
    }
  };

  const moveRubricUp = (index) => {
    if (index <= 0) return;
    const newRubrics = [...selectedRubrics];
    const temp = newRubrics[index];
    newRubrics[index] = newRubrics[index - 1];
    newRubrics[index - 1] = temp;
    setSelectedRubrics(newRubrics);
  };

  const moveRubricDown = (index) => {
    if (index >= selectedRubrics.length - 1) return;
    const newRubrics = [...selectedRubrics];
    const temp = newRubrics[index];
    newRubrics[index] = newRubrics[index + 1];
    newRubrics[index + 1] = temp;
    setSelectedRubrics(newRubrics);
  };

  const handleDragStart = (index) => {
    setDragIndex(index);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = (index) => {
    if (dragIndex === null || dragIndex === index) return;
    const updated = [...selectedRubrics];
    const [moved] = updated.splice(dragIndex, 1);
    updated.splice(index, 0, moved);
    setSelectedRubrics(updated);
    setDragIndex(null);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
  };

  const RubricList = () => {
    if (!selectedRubrics.length) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
          {t('Aucune rubrique sélectionnée. Cliquez sur "Sélectionner les rubriques" pour en ajouter.')}
        </Typography>
      );
    }

    return (
      <List sx={{ bgcolor: 'background.paper', minHeight: '100px' }} dense disablePadding>
        {selectedRubrics.map((rubricId, index) => {
          const rubric = rubrics.find(r => r.id === rubricId);
          if (!rubric) return null;
          const statusChip = rubric.obsolete ? (
            <Chip
              label={t('Cachée')}
              color="error"
              size="small"
              sx={{ minWidth: 0 }}
            />
          ) : (
            <Chip
              label={t('Visible')}
              color="success"
              size="small"
              sx={{ minWidth: 0 }}
            />
          );
          
          return (
            <ListItem
              key={`rubric-item-${index}-${rubricId}`}
              sx={{
                border: '1px solid #e0e0e0',
                mb: 0.75,
                borderRadius: 1,
                pr: 1.5,
                pl: 1.5,
                py: 1.1,
                width: '100%',
                cursor: 'grab',
                opacity: dragIndex === index ? 0.85 : 1,
                '&:active': { cursor: 'grabbing' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1
              }}
              dense
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={handleDragOver}
              onDrop={() => handleDrop(index)}
              onDragEnd={handleDragEnd}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                <DragIndicatorIcon fontSize="small" color="action" />
              </Box>
              <ListItemText 
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {rubric.title}
                    </Typography>
                    {rubric.version && (
                      <Chip
                        label={`${t('Version')}: ${rubric.version}`}
                        size="small"
                        variant="outlined"
                        sx={{ minWidth: 0 }}
                      />
                    )}
                    {statusChip}
                  </Box>
                }
              />
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'nowrap' }}>
                <IconButton
                  onClick={() => moveRubricUp(index)}
                  disabled={index === 0}
                  size="small"
                >
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
                <IconButton
                  onClick={() => moveRubricDown(index)}
                  disabled={index === selectedRubrics.length - 1}
                  size="small"
                >
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
                <IconButton
                  edge="end"
                  onClick={() => {
                    setSelectedRubrics(prev => 
                      prev.filter((_, i) => i !== index)
                    );
                  }}
                  size="small"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </ListItem>
          );
        })}
      </List>
    );
  };
  const normalizeText = (text) => {
    if (!text) return '';
    return text
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9]/g, '')
      .toLowerCase();
  };

  const filteredRubrics = useMemo(() => {
    const query = rubricSearch.trim().toLowerCase();
    const normalizedQuery = normalizeText(rubricSearch);
    if (!query && !normalizedQuery) return rubrics;
    return rubrics.filter((rubric) => {
      const title = rubric.title?.toLowerCase() || '';
      const version = rubric.version?.toLowerCase() || '';
      const normalizedTitle = normalizeText(rubric.title);
      const normalizedVersion = normalizeText(rubric.version);
      return (
        (query && (title.includes(query) || version.includes(query))) ||
        (normalizedQuery && (normalizedTitle.includes(normalizedQuery) || normalizedVersion.includes(normalizedQuery)))
      );
    });
  }, [rubrics, rubricSearch]);

  const sortedRubrics = useMemo(() => {
    return [...filteredRubrics].sort((a, b) => {
      const titleA = (a.title || '').toLowerCase();
      const titleB = (b.title || '').toLowerCase();
      if (titleA && titleB && titleA !== titleB) {
        return titleA.localeCompare(titleB, 'fr');
      }
      const versionA = (a.version || '').toLowerCase();
      const versionB = (b.version || '').toLowerCase();
      return versionA.localeCompare(versionB, 'fr');
    });
  }, [filteredRubrics]);

  const columns = useMemo(() => ([
    { id: 'title', label: t('Titre'), sortable: true },
    { id: 'reference', label: t('Référence'), sortable: true },
    { id: 'comment', label: t('Commentaire'), sortable: true },
    { id: 'rubricsCount', label: t('Nombre de rubriques'), sortable: true },
  ]), [t]);

  const getComparableValue = (standard, property) => {
    if (property === 'rubricsCount') {
      return Array.isArray(standard.rubrics) ? standard.rubrics.length : 0;
    }
    const value = standard[property];
    return value ? value.toString().toLowerCase() : '';
  };

  const { order, orderBy, sortedRows, handleRequestSort } = useTableSort(standards, {
    defaultOrderBy: 'title',
    getComparableValue,
  });

  const renderCellContent = (standard, columnId) => {
    switch (columnId) {
      case 'rubricsCount':
        return standard.rubrics?.length || 0;
      case 'reference':
      case 'comment':
        return standard[columnId] || '-';
      default:
        return standard[columnId];
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Fade in timeout={500}>
        <Box>
          <Typography
            variant="h4"
            gutterBottom
            align="center"
            color="primary"
            sx={{ fontWeight: 'bold', mb: 4 }}
          >
            {t('Gestion des Canevas')}
          </Typography>

          <Info type="standard" />

          <LanguageSelector 
            language={language} 
            onLanguageChange={setLanguage} 
          />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t(error)}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t(success)}
            </Alert>
          )}

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              {t(isEditing ? 'Modifier le Canevas' : 'Ajouter un Canevas')}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="title"
                  label={t('Titre')}
                  value={formData.title}
                  onChange={handleInputChange}
                  variant="outlined"
                  required
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="reference"
                  label={t('Référence')}
                  value={formData.reference}
                  onChange={handleInputChange}
                  variant="outlined"
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="comment"
                  label={t('Commentaire')}
                  value={formData.comment}
                  onChange={handleInputChange}
                  variant="outlined"
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  onClick={() => setOpenDialog(true)}
                  sx={{ mb: 2 }}
                >
                  {t('Sélectionner les rubriques')}
                </Button>

                <Paper 
                  sx={{ 
                    p: 2, 
                    bgcolor: 'background.default',
                    border: '1px dashed',
                    borderColor: 'divider'
                  }}
                >
                  <RubricList />
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={loading}
                  startIcon={loading && <CircularProgress size={20} />}
                >
                  {loading ? t('Enregistrement...') : t(isEditing ? 'Modifier' : 'Ajouter')}
                </Button>
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography variant="h6" gutterBottom>
              {t('Liste des Canevas')}
            </Typography>

            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        sortDirection={orderBy === column.id ? order : false}
                      >
                        {column.sortable ? (
                          <TableSortLabel
                            active={orderBy === column.id}
                            direction={orderBy === column.id ? order : 'asc'}
                            onClick={() => handleRequestSort(column.id)}
                          >
                            {column.label}
                          </TableSortLabel>
                        ) : (
                          column.label
                        )}
                      </TableCell>
                    ))}
                    <TableCell>{t('Actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedRows.map((standard) => (
                    <TableRow key={standard.id}>
                      {columns.map((column) => (
                        <TableCell key={`${standard.id}-${column.id}`}>
                          {renderCellContent(standard, column.id)}
                        </TableCell>
                      ))}
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'nowrap' }}>
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(standard)}
                            color="primary"
                            disabled={loading}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(standard.id)}
                            color="error"
                            disabled={loading}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Dialog
            open={openDialog}
            onClose={() => setOpenDialog(false)}
            maxWidth="lg"
            fullWidth
            PaperProps={{ sx: { maxHeight: '95vh' } }}
          >
            <DialogTitle>{t('Sélectionner les rubriques')}</DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
              <TextField
                fullWidth
                value={rubricSearch}
                onChange={(e) => setRubricSearch(e.target.value)}
                label={t('Rechercher une rubrique')}
                placeholder={t('Nom ou version')}
                variant="outlined"
                autoComplete="off"
                sx={{ mb: 2 }}
              />
              <Paper
                variant="outlined"
                sx={{ maxHeight: 640, overflowY: 'auto', bgcolor: 'background.default' }}
              >
                <List dense disablePadding>
                  {sortedRubrics.length === 0 && (
                    <ListItem>
                      <ListItemText
                        primary={t('Aucune rubrique trouvée pour cette recherche.')}
                        primaryTypographyProps={{ color: 'text.secondary' }}
                      />
                    </ListItem>
                  )}
                  {sortedRubrics.map((rubric) => {
                    const isSelected = selectedRubrics.includes(rubric.id);
                    const statusChip = rubric.obsolete ? (
                      <Chip
                        label={t('Cachée')}
                        color="error"
                        size="small"
                        sx={{ minWidth: 0 }}
                      />
                    ) : (
                      <Chip
                        label={t('Visible')}
                        color="success"
                        size="small"
                        sx={{ minWidth: 0 }}
                      />
                    );

                    return (
                      <ListItem
                        key={rubric.id}
                        dense
                        divider
                        secondaryAction={
                          <Button
                            variant={isSelected ? 'contained' : 'outlined'}
                            onClick={() => {
                              if (isSelected) {
                                setSelectedRubrics(prev => prev.filter(id => id !== rubric.id));
                              } else {
                                setSelectedRubrics(prev => [...prev, rubric.id]);
                              }
                            }}
                            size="small"
                          >
                            {isSelected ? t('Sélectionnée') : t('Sélectionner')}
                          </Button>
                        }
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {rubric.title}
                              </Typography>
                              {rubric.version && (
                                <Chip
                                  label={`${t('Version')}: ${rubric.version}`}
                                  size="small"
                                  variant="outlined"
                                  sx={{ minWidth: 0 }}
                                />
                              )}
                              {statusChip}
                            </Box>
                          }
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </Paper>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenDialog(false)}>{t('Fermer')}</Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Fade>
    </Container>
  );
}

export default StandardAdmin;